import { z } from 'zod';
import { brandedUuid } from './ids.ts';

/**
 * Engagement & achievements contracts — the typed shapes that cross the
 * engagement seam (engagement.md). The MATCHING logic lives behind the backend
 * `services/engagement/*` (evaluate / reward / comment) and the streak-tick
 * job; this file owns only the wire SHAPE so a domain event emitter, the single
 * `EngagementService` consumer, and the `apps/jobs` companion-comment worker all
 * agree on what an event and the comment job payload look like.
 *
 * Achievement thresholds themselves are NOT modelled here — they live in
 * `achievements.threshold` (jsonb), seeded from a catalog, so a new milestone is
 * a seed row, not a code change (engagement.md § "Catalog").
 */

/**
 * The metric a domain event carries — the emittable achievement signals. A
 * catalog `threshold` keys off one of these (e.g. `{ streak_days: 7 }`); adding a
 * new metric needs a new emitter (code), but a new THRESHOLD on an existing
 * metric is a seed row only. `value` in an {@link EngagementEvent} is compared
 * against `threshold[metric]`.
 */
export const EngagementMetric = z.enum([
  /** Cumulative user messages sent (chat activity). */
  'messages',
  /** Companions built (`girlfriends` count). */
  'companions',
  /** Images generated (`media_jobs` done, image). */
  'images',
  /** Videos stitched (`media_jobs` done, video). */
  'videos',
  /** Consecutive active days — emitted by the daily streak tick, in user-tz days. */
  'streak_days',
  /** Qualified referrals (`referrals.status='qualified'`). */
  'referrals_qualified',
  /** Subscription tier rank — emitted on an upgrade (`spend` category). */
  'tier_rank',
]);
export type EngagementMetric = z.infer<typeof EngagementMetric>;

/**
 * One engagement event a domain service EMITS to the single consumer
 * ({@link https://engagement.md `EngagementService.evaluate`}) — domain services
 * never award achievements themselves (DIP). `value` is the CURRENT cumulative
 * metric value (not a delta): evaluate advances progress to it and unlocks when
 * it meets a catalog threshold, so a replayed event is idempotent (re-asserting
 * the same value never double-unlocks).
 */
export const EngagementEvent = z.object({
  metric: EngagementMetric,
  /** The current cumulative value of `metric` (compared to `threshold[metric]`). */
  value: z.number().int().nonnegative(),
  /**
   * The companion in context for a companion-scoped event; the in-character
   * unlock comment prefers her. Null/absent for account-level events (streak,
   * referrals, tier) — the comment then falls back to the active companion.
   */
  girlfriendId: brandedUuid('GirlfriendId').nullable().optional(),
  /**
   * The device fingerprint hash of the live request that triggered the event, if
   * any — the anti-farming guard on a credit-bearing unlock. Absent for
   * background emitters (the streak tick), which fall back to the account's
   * recorded fingerprints.
   */
  fingerprintHash: z.string().min(1).optional(),
});
export type EngagementEvent = z.infer<typeof EngagementEvent>;

/**
 * The BullMQ payload for one companion-comment — enqueued by
 * `EngagementCommentService` when an achievement unlocks, consumed by the
 * `apps/jobs` companion-comment worker (which runs the active girlfriend's agent
 * to post an in-character `origin='proactive'` message and stores its id on
 * `user_achievements.comment_msg_id`). `userAchievementId` is the stable
 * idempotency key: a double-enqueue or a retry collapses to ONE comment, and the
 * worker's `comment_msg_id` post-once guard blocks a duplicate message.
 */
export const CompanionCommentJobData = z.object({
  accountId: brandedUuid('AccountId'),
  userId: brandedUuid('UserId'),
  /** The companion who posts the in-character comment (resolved at enqueue time). */
  girlfriendId: brandedUuid('GirlfriendId'),
  /** The unlock row the comment reacts to — the post-once + idempotency key. */
  userAchievementId: brandedUuid('UserAchievementId'),
  /** Stable catalog key of the unlocked achievement (for the system note + logs). */
  achievementKey: z.string().min(1),
  /** Human title of the achievement — what she reacts to ("Week One"). */
  achievementTitle: z.string().min(1),
});
export type CompanionCommentJobData = z.infer<typeof CompanionCommentJobData>;
